import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import {
  batchRestoreAnswers,
  getStudentDeletedAnswers,
  keepNewerDeletedAnswers,
  restoreAnswer,
  searchTeacherStudents
} from "../services/TeacherAPIClient";
import { formatClassDisplayName } from "../utils/classDisplay";
import {
  assertConfirmedMutationResponse,
  getWorkflowAuditReference
} from "../utils/teacherWorkflowRequests";
import "./TeacherRestoreChildResultsModal.css";

const STUDENT_SEARCH_MIN_LENGTH = 2;
const STUDENT_SEARCH_LIMIT = 25;
const STUDENT_SEARCH_DEBOUNCE_MS = 300;

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ");

const normaliseName = (value) => normaliseText(value).toLowerCase();

const normalisePrefill = (prefill) => ({
  studentName: normaliseText(prefill?.studentName || prefill?.student_name),
  className: normaliseText(prefill?.className || prefill?.class_name),
  assessmentName: normaliseText(prefill?.assessmentName || prefill?.assessment_name),
  taskName: normaliseText(prefill?.taskName || prefill?.task_name),
  reason: normaliseText(prefill?.reason || prefill?.note)
});

const normaliseChild = (child, countryGrades) => ({
  ...child,
  id: child?.id || child?._id || child?.studentid || child?.studentId,
  name: child?.name || child?.fullName || child?.displayName || "Unnamed child",
  classId: child?.classId || child?.classid || child?.class_id,
  className: child?.className || child?.class_name || "Class unavailable",
  classDisplayName: formatClassDisplayName(child, "Class unavailable", countryGrades),
  grade: child?.grade || child?.gradeName || child?.grade_name || "",
  academicYear: child?.academicYear || child?.academic_year || ""
});

const getTaskId = (task, index) =>
  task?.id ||
  task?._id ||
  task?.taskId ||
  task?.taskid ||
  task?.task_id ||
  task?.answerid ||
  task?.answerId ||
  task?.answer_id ||
  `task-${index}`;

const getDeletedAnswerIds = (task) => {
  const ids =
    task?.deletedAnswerIds ||
    task?.deleted_answer_ids ||
    task?.answerIds ||
    task?.answer_ids ||
    task?.answers;

  if (Array.isArray(ids)) {
    return ids
      .map((item) =>
        typeof item === "object"
          ? item.id || item._id || item.answerid || item.answerId || item.answer_id
          : item
      )
      .filter(Boolean);
  }

  return [task?.deletedAnswerId || task?.deleted_answer_id || task?.answerId || task?.answer_id || task?.id]
    .filter(Boolean);
};

const getTaskName = (task) =>
  task?.name ||
  task?.taskName ||
  task?.task_name ||
  task?.title ||
  "Task";

const hasTruthyField = (item, fields) =>
  fields.some((field) => item?.[field] === true || Boolean(item?.[field]));

const hasFalseField = (item, fields) =>
  fields.some((field) => item?.[field] === false);

const getFirstTruthyField = (item, fields) =>
  fields.map((field) => item?.[field]).find(Boolean);

const formatDate = (dateValue) => {
  if (!dateValue) {
    return "";
  }

  const parsedDate = new Date(dateValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return normaliseText(dateValue);
  }

  return parsedDate.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric"
  });
};

const statusIncludes = (task, words) => {
  const statusText = normaliseName(
    [
      task?.status,
      task?.state,
      task?.restoreStatus,
      task?.restore_status,
      task?.deletionStatus,
      task?.deletion_status
    ].filter(Boolean).join(" ")
  );

  return words.some((word) => statusText.includes(word));
};

const hasNewerAttempt = (task) =>
  hasTruthyField(task, [
    "hasNewerAttempt",
    "has_newer_attempt",
    "completedAgain",
    "completed_again",
    "hasConflict",
    "has_conflict"
  ]) ||
  Boolean(
    task?.newerAttempt ||
    task?.newer_attempt ||
    task?.newerResult ||
    task?.newer_result ||
    task?.currentAttempt ||
    task?.current_attempt
  );

const isTaskRestorable = (task) => {
  if (
    hasFalseField(task, [
      "canRestore",
      "can_restore",
      "restorable",
      "restoreAvailable",
      "restore_available"
    ]) ||
    statusIncludes(task, ["not restorable", "already restored"])
  ) {
    return false;
  }

  if (
    hasTruthyField(task, [
      "canRestore",
      "can_restore",
      "restorable",
      "restoreAvailable",
      "restore_available"
    ])
  ) {
    return true;
  }

  return (
    hasTruthyField(task, [
      "deleted",
      "isDeleted",
      "is_deleted",
      "wasDeleted",
      "was_deleted",
      "reset",
      "isReset",
      "is_reset",
      "wasReset",
      "was_reset"
    ]) ||
    Boolean(
      getFirstTruthyField(task, [
        "deletedAt",
        "deleted_at",
        "deletionDate",
        "deletion_date",
        "resetAt",
        "reset_at",
        "resetDate",
        "reset_date"
      ])
    ) ||
    statusIncludes(task, ["deleted", "reset", "hidden", "removed"])
  );
};

const getTaskStatusLabel = (task) => {
  if (task?.resultCheckFailed) {
    return "Could not check results";
  }

  if (isTaskRestorable(task)) {
    return hasNewerAttempt(task) ? "Restore available - newer answers found" : "Restore available";
  }

  if (statusIncludes(task, ["already restored"])) {
    return "Already restored";
  }

  return "Not reset or deleted";
};

const getTaskDateLabel = (task) => {
  const dateValue = getFirstTruthyField(task, [
    "deletedAt",
    "deleted_at",
    "deletionDate",
    "deletion_date",
    "resetAt",
    "reset_at",
    "resetDate",
    "reset_date",
    "completedAt",
    "completed_at",
    "dateCompleted",
    "date_completed"
  ]);

  return formatDate(dateValue);
};

const getNewerAttemptLabel = (task) => {
  const newerAttempt =
    task?.newerAttempt ||
    task?.newer_attempt ||
    task?.newerResult ||
    task?.newer_result ||
    task?.currentAttempt ||
    task?.current_attempt ||
    {};
  const dateLabel = formatDate(
    getFirstTruthyField(newerAttempt, [
      "completedAt",
      "completed_at",
      "dateCompleted",
      "date_completed",
      "createdAt",
      "created_at"
    ]) ||
    getFirstTruthyField(task, [
      "newerCompletedAt",
      "newer_completed_at",
      "newerAttemptDate",
      "newer_attempt_date"
    ])
  );

  return dateLabel || "Newer answers found";
};

const getTaskSelectionLabel = (tasks) => {
  if (tasks.length === 1) {
    return tasks[0].name;
  }

  return `${tasks.length} tasks`;
};

const normaliseTask = (task, index) => ({
  ...task,
  id: getTaskId(task, index),
  name: getTaskName(task),
  restorable: isTaskRestorable(task),
  hasNewerAttempt: hasNewerAttempt(task),
  statusLabel: getTaskStatusLabel(task),
  dateLabel: getTaskDateLabel(task),
  newerAttemptLabel: getNewerAttemptLabel(task)
});

const normaliseDeletedAnswersToAssessments = (response) => {
  const rows = Array.isArray(response) ? response : response?.data || [];
  const groups = rows.reduce((grouped, row, index) => {
    const assessmentId = row.assessmentInstanceId || row.assessment_instance_id || row.assessmentId || row.assessment_id || `deleted-assessment-${index}`;
    const taskId = row.taskId || row.task_id || row.taskName || row.task_name || row.id || `deleted-task-${index}`;

    if (!grouped[assessmentId]) {
      grouped[assessmentId] = {
        id: assessmentId,
        name: row.assessmentName || row.assessment_name || "Screener",
        tasksById: {}
      };
    }

    if (!grouped[assessmentId].tasksById[taskId]) {
      grouped[assessmentId].tasksById[taskId] = {
        ...row,
        id: taskId,
        name: row.taskName || row.task_name || "Task",
        restorable: row.restorable !== false,
        deletedAnswerIds: [],
        hasNewerAttempt: Boolean(row.hasNewerAttempt || row.has_newer_attempt || row.newerAttempt || row.newer_attempt),
        newerAttemptLabel: getNewerAttemptLabel(row)
      };
    }

    grouped[assessmentId].tasksById[taskId].deletedAnswerIds.push(row.id || row.deletedAnswerId || row.deleted_answer_id || row.answerid);
    return grouped;
  }, {});

  return Object.values(groups).map((assessment) => {
    const tasks = Object.values(assessment.tasksById).map((task, index) => {
      const normalisedTask = normaliseTask({
        ...task,
        canRestore: task.restorable,
        deleted: task.restorable
      }, index);

      return {
        ...normalisedTask,
        deletedAnswerIds: task.deletedAnswerIds.filter(Boolean)
      };
    });

    return {
      id: assessment.id,
      name: assessment.name,
      tasks,
      restorableTaskCount: tasks.filter((task) => task.restorable).length
    };
  });
};

const classMatchesPrefill = (child, className) => {
  const targetClassName = normaliseName(className);

  if (!targetClassName) {
    return true;
  }

  return (
    normaliseName(child.className) === targetClassName ||
    normaliseName(child.classDisplayName).includes(targetClassName)
  );
};

const findSingleChildMatch = (children, prefill) => {
  const targetChildName = normaliseName(prefill.studentName);

  if (!targetChildName) {
    return null;
  }

  const matches = children.filter(
    (child) =>
      normaliseName(child.name) === targetChildName &&
      classMatchesPrefill(child, prefill.className)
  );

  return matches.length === 1 ? matches[0] : null;
};

const findSingleAssessmentMatch = (assessments, assessmentName) => {
  const targetAssessmentName = normaliseName(assessmentName);

  if (!targetAssessmentName) {
    return null;
  }

  const matches = assessments.filter(
    (assessment) => normaliseName(assessment.name) === targetAssessmentName
  );

  return matches.length === 1 ? matches[0] : null;
};

const findSingleTaskMatch = (assessment, taskName) => {
  const targetTaskName = normaliseName(taskName);

  if (!assessment || !targetTaskName) {
    return null;
  }

  const matches = assessment.tasks.filter(
    (task) => normaliseName(task.name) === targetTaskName
  );

  return matches.length === 1 ? matches[0] : null;
};

const getAssessmentStatusLabel = (assessment) => {
  if (assessment.tasks.length < 1) {
    return "No tasks found";
  }

  if (assessment.restorableTaskCount === 0) {
    return "No restorable tasks";
  }

  return assessment.restorableTaskCount === 1
    ? "1 restorable task"
    : `${assessment.restorableTaskCount} restorable tasks`;
};

function TeacherRestoreChildResultsModal({
  show,
  handleClose,
  prefill = null,
  schoolid = null,
  countryGrades = null,
  teacherid = null,
  teacherName = "",
  onViewStudentResults = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("form");
  const [searchTerm, setSearchTerm] = useState("");
  const [matchingChildren, setMatchingChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState("");
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [conflictChoice, setConflictChoice] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [auditReference, setAuditReference] = useState("");
  const [reason, setReason] = useState("");
  const prefillAppliedRef = useRef({ child: false, task: false });
  const prefillContext = useMemo(() => normalisePrefill(prefill), [prefill]);

  const trimmedSearchTerm = searchTerm.trim();
  const trimmedReason = reason.trim();
  const selectedAssessment = useMemo(
    () => assessments.find((assessment) => assessment.id === selectedAssessmentId) || null,
    [assessments, selectedAssessmentId]
  );
  const selectedTasks = useMemo(
    () => selectedAssessment?.tasks.filter((task) => selectedTaskIds.includes(task.id)) || [],
    [selectedAssessment, selectedTaskIds]
  );
  const selectedTasksWithNewerAnswers = useMemo(
    () => selectedTasks.filter((task) => task.hasNewerAttempt),
    [selectedTasks]
  );
  const selectedTasksWithoutNewerAnswers = useMemo(
    () => selectedTasks.filter((task) => !task.hasNewerAttempt),
    [selectedTasks]
  );
  const canRestore = Boolean(
    selectedChild &&
    selectedAssessment &&
    selectedTasks.length > 0 &&
    selectedTasks.every((task) => task.restorable) &&
    trimmedReason &&
    !isSubmitting
  );

  const resetForm = useCallback(() => {
    setStage("form");
    setSearchTerm(prefillContext.studentName);
    setMatchingChildren([]);
    setSelectedChild(null);
    setAssessments([]);
    setSelectedAssessmentId("");
    setSelectedTaskIds([]);
    setConflictChoice("");
    setIsSearching(false);
    setIsLoadingResults(false);
    setIsSubmitting(false);
    setAuditReference("");
    setReason(prefillContext.reason);
    setError("");
    prefillAppliedRef.current = { child: false, task: false };
  }, [prefillContext.studentName, prefillContext.reason]);

  useEffect(() => {
    if (show) {
      resetForm();
    }
  }, [show, resetForm]);

  useEffect(() => {
    if (!show || trimmedSearchTerm.length < STUDENT_SEARCH_MIN_LENGTH) {
      setMatchingChildren([]);
      setIsSearching(false);
      return undefined;
    }

    let isCurrentSearch = true;
    setIsSearching(true);
    setError("");

    const timeoutId = window.setTimeout(() => {
      searchTeacherStudents(trimmedSearchTerm, STUDENT_SEARCH_LIMIT)
        .then((children) => {
          if (!isCurrentSearch) {
            return;
          }

          const normalisedChildren = (Array.isArray(children) ? children : [])
            .map((child) => normaliseChild(child, countryGrades))
            .filter((child) => child.id);

          setMatchingChildren(normalisedChildren);
        })
        .catch((searchError) => {
          if (!isCurrentSearch) {
            return;
          }

          console.error("Error searching restore child results child:", searchError);
          setMatchingChildren([]);
          setError("Child search could not be loaded. Please try again.");
        })
        .finally(() => {
          if (isCurrentSearch) {
            setIsSearching(false);
          }
        });
    }, STUDENT_SEARCH_DEBOUNCE_MS);

    return () => {
      isCurrentSearch = false;
      window.clearTimeout(timeoutId);
    };
  }, [show, trimmedSearchTerm, countryGrades]);

  useEffect(() => {
    if (
      !show ||
      prefillAppliedRef.current.child ||
      selectedChild ||
      matchingChildren.length < 1
    ) {
      return;
    }

    prefillAppliedRef.current.child = true;
    const matchedChild = findSingleChildMatch(matchingChildren, prefillContext);

    if (matchedChild) {
      setSelectedChild(matchedChild);
    }
  }, [show, matchingChildren, selectedChild, prefillContext]);

  useEffect(() => {
    if (!show || !selectedChild) {
      setAssessments([]);
      setSelectedAssessmentId("");
      setSelectedTaskIds([]);
      return undefined;
    }

    let isCurrentLoad = true;
    setIsLoadingResults(true);
    setAssessments([]);
    setSelectedAssessmentId("");
    setSelectedTaskIds([]);
    setError("");

    getStudentDeletedAnswers(selectedChild.id)
      .then((results) => {
        if (!isCurrentLoad) {
          return;
        }

        const normalisedAssessments = normaliseDeletedAnswersToAssessments(results)
          .filter((assessment) => assessment.id);

        setAssessments(normalisedAssessments);
      })
      .catch((resultsError) => {
        if (!isCurrentLoad) {
          return;
        }

        console.error("Error loading deleted answers to restore:", resultsError);
        setAssessments([]);
        setError("Restorable answers could not be loaded for this child. Please try again.");
      })
      .finally(() => {
        if (isCurrentLoad) {
          setIsLoadingResults(false);
        }
      });

    return () => {
      isCurrentLoad = false;
    };
  }, [show, selectedChild]);

  useEffect(() => {
    if (
      !show ||
      prefillAppliedRef.current.task ||
      !selectedChild ||
      isLoadingResults ||
      assessments.length < 1
    ) {
      return;
    }

    prefillAppliedRef.current.task = true;
    const matchedAssessment = findSingleAssessmentMatch(
      assessments,
      prefillContext.assessmentName
    );
    const matchedTask = findSingleTaskMatch(matchedAssessment, prefillContext.taskName);

    if (matchedAssessment) {
      setSelectedAssessmentId(matchedAssessment.id);
    }

    if (matchedTask) {
      setSelectedTaskIds([matchedTask.id]);
    }
  }, [
    show,
    assessments,
    selectedChild,
    isLoadingResults,
    prefillContext.assessmentName,
    prefillContext.taskName
  ]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setMatchingChildren([]);
    setSelectedChild(null);
    setAssessments([]);
    setSelectedAssessmentId("");
    setSelectedTaskIds([]);
    setError("");
    prefillAppliedRef.current = { child: true, task: false };
  };

  const handleChildSelect = (child) => {
    setSelectedChild(child);
    setSelectedAssessmentId("");
    setSelectedTaskIds([]);
    setConflictChoice("");
    setError("");
  };

  const handleAssessmentSelect = (assessment) => {
    setSelectedAssessmentId(assessment.id);
    setSelectedTaskIds([]);
    setConflictChoice("");
    setError("");
  };

  const handleTaskSelect = (task) => {
    if (!task.restorable) {
      return;
    }

    setSelectedTaskIds((currentTaskIds) =>
      currentTaskIds.includes(task.id)
        ? currentTaskIds.filter((taskId) => taskId !== task.id)
        : [...currentTaskIds, task.id]
    );
    setConflictChoice("");
    setError("");
  };

  const getDeletedAnswerIdsForTasks = (tasks) =>
    [...new Set(tasks.flatMap(getDeletedAnswerIds))];

  const getAuditReferences = (responses) =>
    [...new Set(responses.map(getWorkflowAuditReference).filter(Boolean))].join(", ");

  const restoreTasks = async (tasks) => {
    const answerIds = getDeletedAnswerIdsForTasks(tasks);

    if (answerIds.length < 1) {
      throw new Error("No deleted answer id was available for the selected task.");
    }

    if (answerIds.length === 1) {
      return restoreAnswer(answerIds[0], { reason: trimmedReason });
    }

    return batchRestoreAnswers(schoolid, answerIds, { reason: trimmedReason });
  };

  const recordKeepNewerDecision = async (tasks) => {
    const answerIds = getDeletedAnswerIdsForTasks(tasks);

    if (answerIds.length < 1) {
      throw new Error("No deleted answer id was available for the selected task.");
    }

    const response = await keepNewerDeletedAnswers(selectedChild.id, {
      deletedAnswerIds: answerIds,
      assessmentInstanceId: selectedAssessment?.id,
      reason: trimmedReason,
      note: [selectedAssessment?.name, getTaskSelectionLabel(tasks), trimmedReason].filter(Boolean).join(" | ")
    });
    assertConfirmedMutationResponse(response, ["recorded"]);

    return response;
  };

  const handleRestoreClick = async () => {
    if (!canRestore) {
      return;
    }

    if (selectedTasksWithNewerAnswers.length > 0) {
      setConflictChoice("");
      setStage("conflict");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const response = await restoreTasks(selectedTasks);
      setAuditReference(getAuditReferences([response]));
      setStage("success");
    } catch (restoreError) {
      console.error("Error restoring child results:", restoreError);
      setError("The selected answers could not be restored. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConflictSubmit = async () => {
    if (!conflictChoice) {
      return;
    }

    if (conflictChoice === "keep-newer") {
      setIsSubmitting(true);
      setError("");

      try {
        const responses = [];

        if (selectedTasksWithNewerAnswers.length > 0) {
          responses.push(await recordKeepNewerDecision(selectedTasksWithNewerAnswers));
        }

        if (selectedTasksWithoutNewerAnswers.length > 0) {
          responses.push(await restoreTasks(selectedTasksWithoutNewerAnswers));
        }

        setAuditReference(getAuditReferences(responses));
        setStage("success");
      } catch (keepError) {
        console.error("Error recording keep newer answers decision:", keepError);
        setError("The decision to keep newer answers could not be recorded. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const response = await restoreTasks(selectedTasks);
      setAuditReference(getAuditReferences([response]));
      setStage("success");
    } catch (restoreError) {
      console.error("Error restoring original child results:", restoreError);
      setError("The original answers could not be restored. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: auditReference || selectedChild?.id || selectedChild?.name || "",
        message: "Restore child results workflow complete."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const renderSearchResults = () => {
    if (isSearching) {
      return (
        <div className="restore-child-results-search-state">
          <Spinner animation="border" size="sm" />
          <span>Searching children...</span>
        </div>
      );
    }

    if (trimmedSearchTerm.length < STUDENT_SEARCH_MIN_LENGTH) {
      return <p className="restore-child-results-muted">Start typing the child name.</p>;
    }

    if (matchingChildren.length < 1) {
      return (
        <div className="restore-child-results-empty">
          <strong>No matching children found.</strong>
          <span>Try another child name.</span>
        </div>
      );
    }

    return matchingChildren.map((child) => {
      const isSelected = selectedChild?.id === child.id;

      return (
        <button
          key={`${child.classId || "class"}-${child.id}`}
          type="button"
          className={`restore-child-results-child${isSelected ? " selected" : ""}`}
          onClick={() => handleChildSelect(child)}
          aria-label={`Select ${child.name} from ${child.classDisplayName || child.className}`}
          aria-pressed={isSelected}
        >
          <strong>{child.name}</strong>
          <span className="restore-child-results-row">
            <span>Class</span>
            <span>{child.classDisplayName || child.className}</span>
          </span>
          {child.academicYear && (
            <span className="restore-child-results-row">
              <span>Year</span>
              <span>{child.academicYear}</span>
            </span>
          )}
        </button>
      );
    });
  };

  const renderSelectedChild = () => {
    if (!selectedChild) {
      return renderSearchResults();
    }

    return (
      <div className="restore-child-results-selected-card">
        <span className="restore-child-results-pill">Selected</span>
        <strong>{selectedChild.name}</strong>
        <span className="restore-child-results-row">
          <span>Class</span>
          <span>{selectedChild.classDisplayName || selectedChild.className}</span>
        </span>
        <Button
          type="button"
          variant="outline-secondary"
          size="sm"
          onClick={() => {
            setSelectedChild(null);
            setAssessments([]);
            setSelectedAssessmentId("");
            setSelectedTaskIds([]);
            setConflictChoice("");
          }}
        >
          Change
        </Button>
      </div>
    );
  };

  const renderAssessments = () => {
    if (!selectedChild) {
      return <p className="restore-child-results-muted">Choose a child first.</p>;
    }

    if (isLoadingResults) {
      return (
        <div className="restore-child-results-search-state">
          <Spinner animation="border" size="sm" />
          <span>Loading results...</span>
        </div>
      );
    }

    if (assessments.length < 1) {
      return (
        <div className="restore-child-results-empty">
          <strong>No screener results found.</strong>
          <span>This child has no tasks available to check.</span>
        </div>
      );
    }

    return assessments.map((assessment) => {
      const isSelected = selectedAssessmentId === assessment.id;

      return (
        <button
          key={assessment.id}
          type="button"
          className={`restore-child-results-screener${isSelected ? " selected" : ""}`}
          onClick={() => handleAssessmentSelect(assessment)}
          aria-label={`Select ${assessment.name}. ${getAssessmentStatusLabel(assessment)}`}
          aria-pressed={isSelected}
        >
          <strong>{assessment.name}</strong>
          <span>{getAssessmentStatusLabel(assessment)}</span>
        </button>
      );
    });
  };

  const renderTasks = () => {
    if (!selectedAssessment) {
      return <p className="restore-child-results-muted">Choose a screener first.</p>;
    }

    if (selectedAssessment.tasks.length < 1) {
      return (
        <div className="restore-child-results-empty">
          <strong>No tasks found.</strong>
          <span>Choose another screener.</span>
        </div>
      );
    }

    return selectedAssessment.tasks.map((task) => {
      const isSelected = selectedTaskIds.includes(task.id);
      const statusClass = task.restorable ? "available" : "disabled";

      return (
        <button
          key={task.id}
          type="button"
          className={[
            "restore-child-results-task",
            `status-${statusClass}`,
            isSelected ? "selected" : ""
          ].filter(Boolean).join(" ")}
          onClick={() => handleTaskSelect(task)}
          aria-label={`${task.restorable ? (isSelected ? "Deselect" : "Select") : "Unavailable"} ${task.name}. ${task.statusLabel}`}
          aria-pressed={isSelected}
          disabled={!task.restorable}
        >
          {!task.restorable && (
            <span className="restore-child-results-unavailable">Unavailable</span>
          )}
          <strong>{task.name}</strong>
          <span className="restore-child-results-row">
            <span>Status</span>
            <span>{task.statusLabel}</span>
          </span>
          {task.dateLabel && (
            <span className="restore-child-results-row">
              <span>Date</span>
              <span>{task.dateLabel}</span>
            </span>
          )}
        </button>
      );
    });
  };

  const renderSelectionSummary = () => {
    if (!selectedChild || !selectedAssessment || selectedTasks.length < 1) {
      return null;
    }

    return (
      <div className="restore-child-results-summary">
        <strong>
          {selectedTasks.length === 1
            ? `${selectedTasks[0].name} answers will be restored.`
            : `${selectedTasks.length} tasks will be restored.`}
        </strong>
        <span className="restore-child-results-row">
          <span>Child</span>
          <span>{selectedChild.name}</span>
        </span>
        <span className="restore-child-results-row">
          <span>Screener</span>
          <span>{selectedAssessment.name}</span>
        </span>
        {selectedTasks.length > 1 && (
          <span className="restore-child-results-row">
            <span>Tasks</span>
            <span>{selectedTasks.map((task) => task.name).join(", ")}</span>
          </span>
        )}
      </div>
    );
  };

  const renderForm = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Restore Child Results</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="restore-child-results-intro">
          Find the child, then choose the screener and tasks to restore.
        </p>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form.Group controlId="teacherRestoreChildSearch">
          <Form.Label>Which child needs results restored?</Form.Label>
          {!selectedChild && (
            <>
              <Form.Text as="div" className="restore-child-results-field-label">
                Search child
              </Form.Text>
              <Form.Control
                type="search"
                value={searchTerm}
                onChange={handleSearchChange}
                aria-label="Search child"
              />
            </>
          )}
        </Form.Group>

        <div className="restore-child-results-list" aria-label="Matching children">
          {renderSelectedChild()}
        </div>

        <div className="restore-child-results-section">
          <Form.Label as="div">Which screener?</Form.Label>
          <div className="restore-child-results-list" aria-label="Matching screeners">
            {renderAssessments()}
          </div>
        </div>

        <div className="restore-child-results-section">
          <Form.Label as="div">Which tasks?</Form.Label>
          <div className="restore-child-results-list" aria-label="Matching tasks">
            {renderTasks()}
          </div>
        </div>

        {renderSelectionSummary()}

        <Form.Group className="restore-child-results-section" controlId="teacherRestoreChildResultsReason">
          <Form.Label>Reason for restoration</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
              setError("");
            }}
            aria-label="Reason for restoring the results"
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <span className="restore-child-results-footer-note">
          {canRestore
            ? `${getTaskSelectionLabel(selectedTasks)} selected.`
            : selectedTasks.length > 0 && !trimmedReason
              ? "Add a reason for the restoration."
              : "Choose at least one restorable task."}
        </span>
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleRestoreClick} disabled={!canRestore}>
          Restore answers
        </Button>
      </Modal.Footer>
    </>
  );

  const renderConflict = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Choose Which Answers To Keep</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="restore-child-results-intro">
          {selectedChild?.name} has newer answers for {getTaskSelectionLabel(selectedTasksWithNewerAnswers)}.
        </p>
        <Alert variant="warning" className="restore-child-results-warning">
          <FaExclamationCircle aria-hidden="true" />
          <span>
            <strong>Which version should be kept?</strong>
            <span>Choose before completing the restore.</span>
          </span>
        </Alert>
        <div className="restore-child-results-choice-list">
          <label className={`restore-child-results-choice${conflictChoice === "restore" ? " selected" : ""}`}>
            <Form.Check
              type="radio"
              name="restoreChildResultsConflict"
              checked={conflictChoice === "restore"}
              onChange={() => setConflictChoice("restore")}
              aria-label="Restore original answers"
            />
            <span>
              <strong>Restore original answers</strong>
              <span>
                Restore the selected reset or deleted answers for {selectedAssessment?.name}.
              </span>
            </span>
          </label>
          <label className={`restore-child-results-choice${conflictChoice === "keep-newer" ? " selected" : ""}`}>
            <Form.Check
              type="radio"
              name="restoreChildResultsConflict"
              checked={conflictChoice === "keep-newer"}
              onChange={() => setConflictChoice("keep-newer")}
              aria-label="Keep newer answers"
            />
            <span>
              <strong>Keep newer answers</strong>
              <span>
                {selectedTasksWithNewerAnswers.length === 1
                  ? selectedTasksWithNewerAnswers[0].newerAttemptLabel
                  : `${selectedTasksWithNewerAnswers.length} tasks with newer answers stay visible.`}
              </span>
            </span>
          </label>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <span className="restore-child-results-footer-note">
          Choose the version to keep.
        </span>
        <Button variant="outline-secondary" onClick={() => setStage("form")}>
          Back
        </Button>
        <Button
          variant="primary"
          onClick={handleConflictSubmit}
          disabled={!conflictChoice}
        >
          {conflictChoice === "keep-newer" ? "Keep newer answers" : "Restore original answers"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSuccess = () => {
    const keptNewerAnswers = conflictChoice === "keep-newer";
    const selectedTaskNames = selectedTasks.map((task) => task.name).join(", ");
    const restoredAnswersLabel = selectedTasks.length === 1
      ? `${selectedAssessment?.name} ${selectedTaskNames}`
      : selectedAssessment?.name;
    const keptAnswersLabel = selectedTasksWithNewerAnswers.length === 1
      ? `newer ${selectedTasksWithNewerAnswers[0].name}`
      : "newer";

    return (
      <>
        <Modal.Header closeButton>
          <Modal.Title as="h2">Answers Restored</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="restore-child-results-intro">
            {selectedChild?.name}'s results have been updated.
          </p>
          <Alert variant="success" className="restore-child-results-success">
            <FaCheckCircle aria-hidden="true" />
            <span>
              <strong>
                {keptNewerAnswers
                  ? `${selectedChild?.name}'s ${keptAnswersLabel} answers were kept.`
                  : `${selectedChild?.name}'s ${restoredAnswersLabel} answers have been restored.`}
              </strong>
              <span>
                {keptNewerAnswers
                  ? "Decision recorded. Any selected tasks without newer answers were restored."
                  : "The selected tasks are ready to show with the child results."}
                {auditReference ? ` Audit reference: ${auditReference}.` : ""}
              </span>
            </span>
          </Alert>
          <div className="restore-child-results-summary">
            <strong>What happens next</strong>
            <span className="restore-child-results-row">
              <span>Child</span>
              <span>{selectedChild?.name}</span>
            </span>
            <span className="restore-child-results-row">
              <span>Screener</span>
              <span>{selectedAssessment?.name}</span>
            </span>
            <span className="restore-child-results-row">
              <span>Tasks</span>
              <span>{selectedTaskNames}</span>
            </span>
          </div>
        </Modal.Body>
        <Modal.Footer className="restore-child-results-success-footer">
          <span className="restore-child-results-footer-note">
            {embedded ? "Done returns to SENSAI." : "Done returns to Help Centre home."}
          </span>
          <Button variant="outline-secondary" onClick={resetForm}>
            Restore another task
          </Button>
          {!embedded && onViewStudentResults && (
            <Button
              variant="outline-secondary"
              onClick={() => onViewStudentResults()}
            >
              View student results
            </Button>
          )}
          <Button variant="primary" onClick={handleDone}>
            Done
          </Button>
        </Modal.Footer>
      </>
    );
  };

  const renderStage = () => {
    if (stage === "conflict") {
      return renderConflict();
    }

    if (stage === "success") {
      return renderSuccess();
    }

    return renderForm();
  };

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      centered
      className="teacher-restore-child-results-modal"
      backdrop="static"
    >
      {renderStage()}
    </Modal>
  );
}

export default TeacherRestoreChildResultsModal;
